/**
 * AST node types for the ERA SPARQL-subset query profile.
 *
 * The parser produces a `Query` tree; downstream planner and adapters
 * consume it. All nodes are plain serialisable objects (no class instances).
 */

// ---------------------------------------------------------------------------
// ERA entity classes supported by the query profile
// ---------------------------------------------------------------------------

export type EraClass =
  | 'OperationalPoint'
  | 'SectionOfLine'
  | 'Track'
  | 'RunningTrack'
  | 'PlatformEdge'
  | 'Tunnel'
  | 'Bridge'
  | 'ContactLineSystem';

// ---------------------------------------------------------------------------
// Filter expressions
// ---------------------------------------------------------------------------

export interface EqualityFilter {
  kind: 'equality';
  variable: string;
  property: string;
  value: string | number | boolean;
}

export interface RegexFilter {
  kind: 'regex';
  variable: string;
  property: string;
  pattern: string;
  flags: string;
}

export interface ContainsFilter {
  kind: 'contains';
  variable: string;
  property: string;
  value: string;
  caseInsensitive: boolean;
}

export interface RangeFilter {
  kind: 'range';
  variable: string;
  property: string;
  min?: number;
  max?: number;
}

/** WGS-84 bounding box: [minLat, minLon, maxLat, maxLon] */
export interface BboxFilter {
  kind: 'bbox';
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

/** Near-point spatial filter */
export interface NearFilter {
  kind: 'near';
  lat: number;
  lon: number;
  radiusMeters: number;
}

export type FilterExpr =
  | EqualityFilter
  | RegexFilter
  | ContainsFilter
  | RangeFilter
  | BboxFilter
  | NearFilter;

// ---------------------------------------------------------------------------
// Triple pattern (basis of WHERE clause)
// ---------------------------------------------------------------------------

export interface TriplePattern {
  subject: string;    // variable name, e.g. "op"
  predicate: string;  // era: prefixed property or 'a' for rdf:type
  object: string;     // variable name OR literal string
}

// ---------------------------------------------------------------------------
// Optional clause (maps to OPTIONAL { ... })
// ---------------------------------------------------------------------------

export interface OptionalPattern {
  patterns: TriplePattern[];
}

// ---------------------------------------------------------------------------
// Projection (SELECT variables)
// ---------------------------------------------------------------------------

export interface SelectClause {
  variables: string[];   // ['*'] means SELECT *
}

// ---------------------------------------------------------------------------
// WHERE clause
// ---------------------------------------------------------------------------

export interface WhereClause {
  patterns: TriplePattern[];
  optionals: OptionalPattern[];
  filters: FilterExpr[];
}

// ---------------------------------------------------------------------------
// Modifiers
// ---------------------------------------------------------------------------

export interface LimitClause {
  value: number;
}

export interface OffsetClause {
  value: number;
}

export type OrderDirection = 'ASC' | 'DESC';

export interface OrderByClause {
  variable: string;
  direction: OrderDirection;
}

// ---------------------------------------------------------------------------
// Top-level Query node
// ---------------------------------------------------------------------------

export interface Query {
  kind: 'select';
  select: SelectClause;
  where: WhereClause;
  /** Derived from WHERE: which ERA class is being queried */
  eraClass: EraClass | null;
  /** Primary subject variable (e.g. "op") */
  subject: string | null;
  limit?: LimitClause;
  offset?: OffsetClause;
  orderBy?: OrderByClause;
}

// ---------------------------------------------------------------------------
// Parse error
// ---------------------------------------------------------------------------

export type ParseErrorKind = 'syntax' | 'unsupported';

export class ParseError extends Error {
  constructor(
    public readonly kind: ParseErrorKind,
    message: string,
    public readonly position?: number,
  ) {
    super(message);
    this.name = 'ParseError';
  }
}

// ---------------------------------------------------------------------------
// Serialisation helper
// ---------------------------------------------------------------------------

export function serializeQuery(q: Query): string {
  return JSON.stringify(q, null, 2);
}
